/** Retry pricing without storage_gb when legacy API returns 500/503. */

import type { AxiosInstance } from 'axios'

export type PricingSuggestBody = {
  phone_model_id: number
  condition: string
  battery_health?: number
  release_year?: number
  storage_gb?: number
  expected_device_color?: string | null
  in_box_completeness?: 'full' | 'partial' | 'phone_only' | null
}

type AxiosLikeError = {
  response?: { status?: number }
}

function isRetryablePricingError(err: unknown): boolean {
  const status = (err as AxiosLikeError)?.response?.status
  return status === 500 || status === 503
}

export function stripStorageGb<T extends { storage_gb?: number | null }>(body: T): T {
  const next = { ...body }
  delete next.storage_gb
  return next
}

export async function postPricingSuggestResilient(
  apiClient: AxiosInstance,
  body: PricingSuggestBody,
): Promise<{ data?: unknown }> {
  try {
    return await apiClient.post('/pricing/suggest', body)
  } catch (err) {
    if (body.storage_gb == null || !isRetryablePricingError(err)) {
      throw err
    }
    const res = await apiClient.post('/pricing/suggest', stripStorageGb(body))
    if (res.data && typeof res.data === 'object') {
      res.data = { ...(res.data as Record<string, unknown>), pricing_unreliable: true }
    }
    return res
  }
}

export async function postPricingSuggestPhotoResilient(
  apiClient: AxiosInstance,
  params: PricingSuggestBody,
  buildFormData: (params: PricingSuggestBody) => FormData,
  requestConfig: Record<string, unknown>,
): Promise<{ data?: unknown }> {
  try {
    return await apiClient.post(
      '/pricing/suggest-with-photo-preview',
      buildFormData(params),
      requestConfig,
    )
  } catch (err) {
    if (params.storage_gb == null || !isRetryablePricingError(err)) {
      throw err
    }
    const res = await apiClient.post(
      '/pricing/suggest-with-photo-preview',
      buildFormData(stripStorageGb(params)),
      requestConfig,
    )
    if (res.data && typeof res.data === 'object') {
      res.data = { ...(res.data as Record<string, unknown>), pricing_unreliable: true }
    }
    return res
  }
}
